import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const SUGGESTIONS = [
  "How do I list my lodge?",
  "Is the booking engine live?",
  "How do partnerships work?",
];
const GREETING =
  "Welcome to Tourism Connect. I'm the concierge — ask me about partnerships, the booking engine, or our sub-products.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat");
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [msgName, setMsgName] = useState("");
  const [msgEmail, setMsgEmail] = useState("");
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const scrollRef = useRef(null);

  const [sessionId] = useState(() => {
    let s = localStorage.getItem("tc_chat_session");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("tc_chat_session", s);
    }
    return s;
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming, open, mode]);

  const sendChat = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setError("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 45000);
    try {
      const resp = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (!chunk.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(chunk.slice(6));
            if (ev.delta) {
              const d = ev.delta;
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d };
                return c;
              });
            }
            if (ev.error) {
              setMessages((m) => m.slice(0, -1));
              setError(ev.error);
            }
          } catch {
            /* partial JSON, wait for more */
          }
        }
      }
    } catch {
      setMessages((m) => m.slice(0, -1));
      setError("The concierge is unavailable right now — leave a message and we'll reply by email.");
    } finally {
      clearTimeout(timer);
      setStreaming(false);
    }
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    setMsgSending(true);
    try {
      await api.post("/chat/message", { name: msgName, email: msgEmail, message: msgText });
      toast.success("Message sent — we'll get back to you shortly.");
      setMsgName("");
      setMsgEmail("");
      setMsgText("");
      setMode("chat");
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setMsgSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 sm:bottom-8 sm:right-8">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4 flex h-[520px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-goldline bg-panel shadow-[var(--shadow-card)]"
          >
            <div className="flex items-center justify-between border-b border-goldline px-5 py-4">
              <div>
                <p className="font-serif text-lg text-cream">Concierge</p>
                <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-subtle">
                  <span className="live-dot h-1.5 w-1.5 rounded-full bg-green-500" /> Tourism Connect
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-muted transition-colors hover:text-cream"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>

            {mode === "chat" ? (
              <>
                <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      data-testid={`chat-message-${m.role}-${i}`}
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "ml-auto rounded-br-sm bg-gold text-on-gold"
                          : "mr-auto rounded-bl-sm border border-line bg-field text-cream"
                      }`}
                    >
                      {m.content ||
                        (streaming && i === messages.length - 1 ? (
                          <span data-testid="chat-typing-indicator" className="flex gap-1 py-1">
                            {[0, 1, 2].map((d) => (
                              <span
                                key={d}
                                className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold/70"
                                style={{ animationDelay: `${d * 0.15}s` }}
                              />
                            ))}
                          </span>
                        ) : null)}
                    </div>
                  ))}
                  {messages.length === 1 && !streaming && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {SUGGESTIONS.map((s, i) => (
                        <button
                          key={s}
                          data-testid={`chat-suggestion-${i}`}
                          onClick={() => sendChat(s)}
                          className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-gold/90 transition-colors hover:bg-gold/10"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                  {error && (
                    <p data-testid="chat-error" className="rounded-xl border border-terra/40 bg-terra/10 px-4 py-2.5 text-xs text-terra">
                      {error}
                    </p>
                  )}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendChat();
                  }}
                  className="border-t border-goldline p-3"
                >
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Ask the concierge…"
                      maxLength={1000}
                      className="field !py-2.5 !text-sm"
                    />
                    <button
                      data-testid="chat-send-button"
                      type="submit"
                      disabled={streaming || !input.trim()}
                      className="btn-gold !rounded-xl !px-3.5 !py-2.5 disabled:opacity-40"
                      aria-label="Send message"
                    >
                      {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid="chat-switch-to-message"
                    onClick={() => setMode("message")}
                    className="mt-2 w-full text-center font-mono text-[9px] uppercase tracking-[0.25em] text-subtle transition-colors hover:text-gold"
                  >
                    Prefer email? Leave a message
                  </button>
                </form>
              </>
            ) : (
              <form data-testid="chat-message-form" onSubmit={submitMessage} className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-5">
                <p className="text-sm leading-relaxed text-muted">
                  Leave your details and a note — it lands straight in our team's inbox and dashboard.
                </p>
                <input
                  data-testid="chat-message-name-input"
                  required
                  value={msgName}
                  onChange={(e) => setMsgName(e.target.value)}
                  placeholder="Your name"
                  className="field !text-sm"
                />
                <input
                  data-testid="chat-message-email-input"
                  required
                  type="email"
                  value={msgEmail}
                  onChange={(e) => setMsgEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="field !text-sm"
                />
                <textarea
                  data-testid="chat-message-text-input"
                  required
                  rows={4}
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  placeholder="How can we help?"
                  className="field !text-sm"
                />
                <button data-testid="chat-message-submit-button" type="submit" disabled={msgSending} className="btn-gold !py-3 !text-xs disabled:opacity-50">
                  {msgSending ? "Sending…" : "Send message"}
                </button>
                <button
                  type="button"
                  data-testid="chat-switch-to-chat"
                  onClick={() => setMode("chat")}
                  className="text-center font-mono text-[9px] uppercase tracking-[0.25em] text-subtle transition-colors hover:text-gold"
                >
                  Back to chat
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold text-on-gold shadow-[var(--shadow-card)] transition-shadow"
        aria-label="Open chat"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>
    </div>
  );
}
